import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import { Eye, Heart, House, MapPin, Minus, PackageSearch, Phone, Plus, Send, Share2 } from 'lucide-react';
import { Rating } from 'react-simple-star-rating';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReportModal from '../components/ReportModal';
import ReportUserModal from '../components/ReportUserModal';
import ProductCarousel from '../components/ProductCarousel';
import ImageViewer from '../components/ImageViewer';
import Breadcrumbs from '../components/Breadcrumbs';
import LoadingIndicator from '../components/LoadingIndicator';
import EmptyState from '../components/EmptyState';
import {
  addProductFavorite,
  fetchProductDetail,
  incrementProductView,
  rateProduct,
  removeProductFavorite,
  reportProduct
} from '../services/product.api';
import { createClientMessageId, MAX_PRODUCT_DETAIL_MESSAGE_LENGTH, sendMessage } from '../services/chat.api';
import { rateUser, reportUser } from '../services/user.api';
import { ProductDetail as ProductDetailType } from '../services/api.types';
import { ApiError } from '../services/api.error';
import { getAccessToken, getUserId } from '../services/auth';
import { listingAnalyticsParams, trackEvent, trackRouteEvent } from '../services/analytics';
import { useTranslations } from '../translations';
import BackButton from '../components/BackButton';
import {
  PRODUCT_LOCATION_MAP_MOBILE_ZOOM,
  MAP_TILE_ATTRIBUTION,
  PRODUCT_LOCATION_MAP_ZOOM,
  PRODUCT_LOCATION_RADIUS_METERS
} from '../constants/product';
import { useNotification } from '../components/NotificationProvider';
import { appLinkClick, createPageUrl } from '../utils/appRoutes';
import { shareUrl } from '../utils/share';
import { createProductShareUrl } from '../utils/shareUrls';
import { formatUnitType } from '../utils/unitType';
import { getPublishedAt } from '../utils/productDate';
import { getUserPublicName } from '../utils/userSeo';
import ProductImagePlaceholder from '../components/ProductImagePlaceholder';
import AccountTypeInfo from '../components/AccountTypeInfo';
import './ProductDetail.css';

interface ProductDetailProps {
  isLoggedIn: boolean;
  isAdmin?: boolean;
  productId: string;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onNavigateToProductDetail: (productId: string) => void;
  onNavigateToUserDetails?: (userId: string, publicName?: string) => void;
  onLogout: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToLocations?: () => void;
  onNavigateToMyAnnouncements?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToMessages?: (productId?: string | null) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToContact?: () => void;
  onNavigateToAdministration?: () => void;
}

// Survives a page reload but is cleared when the listing is left in-app, so a refresh never re-counts a view.
const COUNTED_VIEW_KEY = 'roade_counted_listing_view';

const ProductDetail: React.FC<ProductDetailProps> = ({
  isLoggedIn,
  isAdmin = false,
  productId,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onNavigateToProductDetail,
  onNavigateToUserDetails,
  onLogout,
  onNavigateToProfile,
  onNavigateToLocations,
  onNavigateToMyAnnouncements,
  onNavigateToSettings,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onNavigateToContact,
  onNavigateToAdministration
}) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [product, setProduct] = useState<ProductDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showPhoneNumber, setShowPhoneNumber] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [productRating, setProductRating] = useState(0);
  const [sellerRating, setSellerRating] = useState(0);
  const [showRatingConfirm, setShowRatingConfirm] = useState(false);
  const [pendingRating, setPendingRating] = useState<{ type: 'product' | 'seller', value: number } | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showReportUserModal, setShowReportUserModal] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteUpdating, setFavoriteUpdating] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const sendingMessageRef = useRef(false);
  const pendingMessageRef = useRef<{ productId: string; message: string; clientMessageId: string } | null>(null);
  const viewedListingIdRef = useRef<string | null>(null);
  const phoneRevealTrackedRef = useRef(false);
  const availableImages = product?.images.filter(({ imageUrl }) => Boolean(imageUrl?.trim())) ?? [];

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  useEffect(() => {
    let cancelled = false;
    const loadProductData = async () => {
      setLoading(true);
      setCurrentImageIndex(0);
      setShowPhoneNumber(false);
      setProductRating(0);
      setSellerRating(0);
      try {
        const data = await fetchProductDetail(productId);
        setProduct(data);
        setIsFavorite(Boolean(data.favorite));
        // Once per detail navigation; a login-triggered refetch of the same listing is not a new view.
        if (!cancelled && viewedListingIdRef.current !== productId) {
          viewedListingIdRef.current = productId;
          phoneRevealTrackedRef.current = false;
          if (sessionStorage.getItem(COUNTED_VIEW_KEY) !== productId) {
            sessionStorage.setItem(COUNTED_VIEW_KEY, productId);
            incrementProductView(productId);
          }
          // Key matches App's page-view key; sent once consent resolves if still on this listing.
          trackRouteEvent(`productDetail:${productId}`, 'listing_view', listingAnalyticsParams(data));
        }

        // Update meta tags for social media sharing
        if (data) {
          const description = data.description.substring(0, 160) + (data.description.length > 160 ? '...' : '');
          const productPath = createPageUrl('productDetail', { id: productId, title: data.title });
          const productUrl = `${window.location.origin}${productPath}`;
          const mainImageUrl = data.images?.find(({ imageUrl }) => Boolean(imageUrl?.trim()))?.imageUrl;
          const listingTitle = `${data.productType === 'DEMAND' ? 'Cumpăr: ' : ''}${data.title.trim().replace(/\s+/g, ' ')}`;
          const place = [data.locationDto?.diacriticsName || data.locationDto?.name, data.locationDto?.county?.name]
            .filter(Boolean)
            .join(', ');

          if (window.location.pathname !== productPath || window.location.search) {
            window.history.replaceState({}, '', productPath);
          }

          let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
          if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
          }
          canonical.href = productUrl;

          // Update or create meta tags (for Open Graph)
          const updateMetaTag = (property: string, content: string) => {
            let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
            if (!meta) {
              meta = document.createElement('meta');
              meta.setAttribute('property', property);
              document.head.appendChild(meta);
            }
            meta.content = content;
          };

          // Update or create meta tags (for Twitter - uses name attribute)
          const updateMetaTagByName = (name: string, content: string) => {
            let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
            if (!meta) {
              meta = document.createElement('meta');
              meta.setAttribute('name', name);
              document.head.appendChild(meta);
            }
            meta.content = content;
          };

          // Update Open Graph tags
          updateMetaTag('og:type', 'website');
          updateMetaTag('og:url', productUrl);
          updateMetaTag('og:site_name', 'roade.ro');
          updateMetaTag('og:title', listingTitle);
          updateMetaTag('og:description', description);
          updateMetaTag('og:locale', 'ro_RO');
          if (mainImageUrl) {
            updateMetaTag('og:image', mainImageUrl);
            updateMetaTag('og:image:secure_url', mainImageUrl);
            updateMetaTag('og:image:alt', data.title);
          }

          // Update Twitter tags (using name attribute)
          updateMetaTagByName('twitter:card', 'summary');
          updateMetaTagByName('twitter:title', listingTitle);
          updateMetaTagByName('twitter:description', description);
          if (mainImageUrl) {
            updateMetaTagByName('twitter:image', mainImageUrl);
            updateMetaTagByName('twitter:image:alt', data.title);
          }

          // Update page title
          document.title = `${listingTitle}${place ? ` – ${place}` : ''} | roade.ro`;
          updateMetaTagByName('description', description);
          updateMetaTagByName(
            'robots',
            !data.status || data.status === 'ACTIVE' ? 'index, follow' : 'noindex, nofollow',
          );

          document.getElementById('product-structured-data')?.remove();
          if (data.productType === 'PRODUCT' && (!data.status || data.status === 'ACTIVE')) {
            const sellerName = data.user.companyName
              || `${data.user.firstName || ''} ${data.user.lastName || ''}`.trim();
            const structuredData: Record<string, unknown> = {
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: data.title,
              description,
              url: productUrl,
              identifier: data.id,
              ...(mainImageUrl ? { image: [mainImageUrl] } : {}),
              ...(data.createdAt ? { datePublished: getPublishedAt(data) } : {}),
              ...(data.updatedAt ? { dateModified: data.updatedAt } : {}),
              offers: {
                '@type': 'Offer',
                url: productUrl,
                price: data.price.price,
                priceCurrency: data.price.currency || 'RON',
                availability: 'https://schema.org/InStock',
                itemCondition: 'https://schema.org/NewCondition',
                ...(sellerName ? {
                  seller: {
                    '@type': data.user.companyName ? 'Organization' : 'Person',
                    name: sellerName,
                  },
                } : {}),
              },
              ...((data.ratingCount || 0) > 0 && data.rate > 0 ? {
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: data.rate,
                  reviewCount: data.ratingCount,
                },
              } : {}),
            };
            const jsonLd = document.createElement('script');
            jsonLd.id = 'product-structured-data';
            jsonLd.type = 'application/ld+json';
            jsonLd.text = JSON.stringify(structuredData);
            document.head.appendChild(jsonLd);
          }
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProductData();

    // Cleanup: Reset meta tags when component unmounts
    return () => {
      cancelled = true;
      document.title = 'roade.ro';
      const resetMetaTag = (property: string, content: string) => {
        const meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
        if (meta) {
          meta.content = content;
        }
      };
      const resetMetaTagByName = (name: string, content: string) => {
        const meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
        if (meta) {
          meta.content = content;
        }
      };
      resetMetaTag('og:type', 'website');
      resetMetaTag('og:url', 'https://roade.ro/');
      resetMetaTag('og:site_name', 'roade.ro');
      resetMetaTag('og:title', 'roade.ro - Platformă de anunțuri');
      resetMetaTag('og:description', 'roade.ro - Platformă de anunțuri pentru produse agricole și piața locală');
      resetMetaTag('og:locale', 'ro_RO');
      resetMetaTagByName('twitter:card', 'summary');
      resetMetaTagByName('twitter:title', 'roade.ro - Platformă de anunțuri');
      resetMetaTagByName('twitter:description', 'roade.ro - Platformă de anunțuri pentru produse agricole și piața locală');
      ['og:image', 'og:image:secure_url', 'og:image:alt'].forEach((property) => {
        document.querySelector(`meta[property="${property}"]`)?.remove();
      });
      ['twitter:image', 'twitter:image:alt'].forEach((name) => {
        document.querySelector(`meta[name="${name}"]`)?.remove();
      });
      document.getElementById('product-structured-data')?.remove();
    };
  }, [productId, isLoggedIn]);

  // Runs on in-app navigation away from the listing, not on reload, so the next entry counts again.
  useEffect(() => () => sessionStorage.removeItem(COUNTED_VIEW_KEY), [productId]);

  useEffect(() => {
    if (!product?.locationDto?.coordinates || !mapContainerRef.current) return;

    if (mapRef.current) {
      mapRef.current.remove();
    }

    const { latitude, longitude } = product.locationDto.coordinates;
    const mapZoom = window.matchMedia('(max-width: 768px)').matches
      ? PRODUCT_LOCATION_MAP_MOBILE_ZOOM
      : PRODUCT_LOCATION_MAP_ZOOM;

    const map = L.map(mapContainerRef.current, {
      center: [latitude, longitude],
      zoom: mapZoom,
      zoomSnap: 0.1,
      zoomControl: false
    });
    map.attributionControl.setPrefix(false);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: MAP_TILE_ATTRIBUTION
    }).addTo(map);

    const locationCircle = L.circle([latitude, longitude], {
      radius: PRODUCT_LOCATION_RADIUS_METERS,
      color: '#B12A00',
      weight: 2,
      opacity: 0.75,
      fillColor: '#B12A00',
      fillOpacity: 0.12,
      interactive: false
    }).addTo(map);

    map.fitBounds(locationCircle.getBounds(), {
      padding: [16, 16],
      maxZoom: mapZoom,
      animate: false
    });
    map.setZoom(mapZoom, { animate: false });

    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [product?.locationDto?.coordinates]);

  const handleZoomIn = () => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  };


  const handlePreviousImage = () => {
    if (availableImages.length > 0 && currentImageIndex > 0) {
      setCurrentImageIndex((prev) => prev - 1);
    }
  };

  const handleNextImage = () => {
    if (availableImages.length > 0 && currentImageIndex < availableImages.length - 1) {
      setCurrentImageIndex((prev) => prev + 1);
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !product) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentImageIndex < availableImages.length - 1) {
      handleNextImage();
    }

    if (isRightSwipe && currentImageIndex > 0) {
      handlePreviousImage();
    }

    // Reset touch states
    setTouchStart(null);
    setTouchEnd(null);
  };


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const time = `${hours}:${minutes}`;

    if (isToday) {
      return t.productDetail.postedToday.replace('{time}', time);
    }

    const day = date.getDate();
    const month = date.toLocaleString('ro-RO', { month: 'short' });
    const dateStr = `${day} ${month}`;
    return t.productDetail.postedOn.replace('{date}', dateStr).replace('{time}', time);
  };

  const formatViewCount = (viewCount: number) => viewCount === 1
    ? t.productDetail.oneView
    : t.productDetail.views.replace('{count}', viewCount.toString());

  const handleShareProduct = async () => {
    if (!product) return;

    const productUrl = createProductShareUrl(
      window.location.origin,
      product.id,
      product.title
    );

    try {
      const result = await shareUrl({
        url: productUrl
      });
      if (result === 'copied') {
        showNotification(t.productDetail.shareLinkCopied, 'success');
      }
    } catch (error) {
      console.error('Error sharing product:', error);
      showNotification(t.productDetail.shareError, 'error');
    }
  };

  const handleFavoriteProduct = async () => {
    if (!product || favoriteUpdating) return;

    if (!isLoggedIn) {
      onNavigateToLogin();
      return;
    }

    const nextFavorite = !isFavorite;
    setIsFavorite(nextFavorite);
    setFavoriteUpdating(true);

    try {
      if (nextFavorite) {
        await addProductFavorite(product.id);
      } else {
        await removeProductFavorite(product.id);
      }
    } catch (error) {
      setIsFavorite(!nextFavorite);
      if (error instanceof ApiError && error.hasErrorCode('product_self_favorite_not_allowed')) {
        showNotification(t.productCard.ownFavoriteNotAllowed, 'error');
      } else {
        showNotification(t.productCard.favoriteUpdateError, 'error');
      }
    } finally {
      setFavoriteUpdating(false);
    }
  };

  const handleProductRating = (rate: number) => {
    setProductRating(rate);
    setPendingRating({ type: 'product', value: rate });
    setShowRatingConfirm(true);
  };

  const handleSellerRating = (rate: number) => {
    setSellerRating(rate);
    setPendingRating({ type: 'seller', value: rate });
    setShowRatingConfirm(true);
  };

  const confirmRating = async () => {
    if (!pendingRating) return;

    if (!isLoggedIn) {
      onNavigateToLogin();
      setShowRatingConfirm(false);
      setPendingRating(null);
      return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      onNavigateToLogin();
      setShowRatingConfirm(false);
      setPendingRating(null);
      return;
    }

    try {
      if (pendingRating.type === 'product') {
        await rateProduct(productId, pendingRating.value);
        setProductRating(0);
      } else {
        await rateUser(product!.user.id, pendingRating.value);
        setSellerRating(0);
      }
      showNotification(t.productDetail.ratingSuccess, 'success');
      void fetchProductDetail(productId)
        .then((updatedProduct) => {
          setProduct(updatedProduct);
          setIsFavorite(Boolean(updatedProduct.favorite));
        })
        .catch((refreshError) => console.error('Error refreshing rating summary:', refreshError));
    } catch (error) {
      console.error('Error rating:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      if (error instanceof ApiError && error.hasErrorCode('product_self_rating_not_allowed')) {
        setProductRating(0);
        showNotification(t.productDetail.productSelfRatingNotAllowed, 'error');
        return;
      }
      if (error instanceof ApiError && error.hasErrorCode('user_self_rating_not_allowed')) {
        setSellerRating(0);
        showNotification(t.productDetail.userSelfRatingNotAllowed, 'error');
        return;
      }
      if (pendingRating.type === 'product') {
        setProductRating(0);
      } else {
        setSellerRating(0);
      }
      showNotification(t.productDetail.ratingError, 'error');
    } finally {
      setShowRatingConfirm(false);
      setPendingRating(null);
    }
  };

  const cancelRating = () => {
    if (pendingRating) {
      if (pendingRating.type === 'product') {
        setProductRating(0);
      } else {
        setSellerRating(0);
      }
    }
    setShowRatingConfirm(false);
    setPendingRating(null);
  };

  const handleSendMessage = async () => {
    if (sendingMessageRef.current || !messageText.trim()) {
      return;
    }

    if (!isLoggedIn) {
      onNavigateToLogin();
      return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      onNavigateToLogin();
      return;
    }

    const pendingMessage = pendingMessageRef.current;
    const clientMessageId = pendingMessage?.productId === productId && pendingMessage.message === messageText
      ? pendingMessage.clientMessageId
      : createClientMessageId();
    pendingMessageRef.current = { productId, message: messageText, clientMessageId };
    sendingMessageRef.current = true;
    setMessageSending(true);

    try {
      await sendMessage({
        message: messageText,
        productId: productId,
        clientMessageId
      });
      pendingMessageRef.current = null;
      setMessageText('');
      showNotification('Mesaj trimis cu succes!', 'success');
      if (product && product.user.id !== getUserId()) {
        trackEvent('contact_seller', {
          ...listingAnalyticsParams(product),
          contact_method: 'message',
          source_surface: 'listing',
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification('Eroare la trimiterea mesajului', 'error');
    } finally {
      sendingMessageRef.current = false;
      setMessageSending(false);
    }
  };

  const handleReportClick = () => {
    setShowReportModal(true);
  };

  const handleReportSubmit = async (reason: string, otherText?: string) => {
    if (!isLoggedIn) {
      onNavigateToLogin();
      setShowReportModal(false);
      return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      onNavigateToLogin();
      setShowReportModal(false);
      return;
    }

    try {
      let reasonText = '';
      if (reason === 'other' && otherText) {
        reasonText = otherText;
      } else {
        const reasonMap: Record<string, string> = {
          spam: t.reportModal.spam,
          wrongCategory: t.reportModal.wrongCategory,
          forbiddenProduct: t.reportModal.forbiddenProduct,
          expiredProduct: t.reportModal.expiredProduct,
          fraud: t.reportModal.fraud,
        };
        reasonText = reasonMap[reason] || reason;
      }
      
      await reportProduct(productId, [reasonText]);
      setShowReportModal(false);
      showNotification(t.reportModal.successMessage, 'success');
    } catch (error) {
      console.error('Error reporting product:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      setShowReportModal(false);
      showNotification(t.reportModal.errorMessage, 'error');
    }
  };

  const handleReportClose = () => {
    setShowReportModal(false);
  };

  const handleReportUserClick = () => {
    if (!isLoggedIn) {
      onNavigateToLogin();
      return;
    }
    setShowReportUserModal(true);
  };

  const handleReportUserSubmit = async (reason: string, otherText?: string) => {
    if (!product) return;

    try {
      const reasonMap: Record<string, string> = {
        spam: t.reportUserModal.spam,
        inappropriateContent: t.reportUserModal.inappropriateContent,
        harassment: t.reportUserModal.harassment,
        scam: t.reportUserModal.scam,
        fakeProfile: t.reportUserModal.fakeProfile,
        sellerStatus: t.reportUserModal.sellerStatus,
      };
      const reasonText = reason === 'other' && otherText ? otherText : (reasonMap[reason] || reason);

      await reportUser(product.user.id, [reasonText]);
      setShowReportUserModal(false);
      showNotification(t.reportUserModal.successMessage, 'success');
    } catch (error) {
      console.error('Error reporting user:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      setShowReportUserModal(false);
      showNotification(t.reportUserModal.errorMessage, 'error');
    }
  };

  const handleReportUserClose = () => {
    setShowReportUserModal(false);
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <Header
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToRegister={onNavigateToRegister}
          onNavigateToHome={onNavigateToHome}
          onLogout={onLogout}
          onProfileClick={onNavigateToProfile}
          onMessagesClick={onNavigateToMessages}
          onNavigateToCreateProduct={onNavigateToCreateProduct}
          onAdministrationClick={onNavigateToAdministration}
        />
        <div className="product-detail-loading-container">
          <LoadingIndicator message={t.productDetail.loading} variant="page" />
        </div>
        <Footer
          onNavigateToTerms={onNavigateToTerms}
          onNavigateToPrivacy={onNavigateToPrivacy}
          onNavigateToCookies={onNavigateToCookies}
          onNavigateToContact={onNavigateToContact}
        />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <Header
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToRegister={onNavigateToRegister}
          onNavigateToHome={onNavigateToHome}
          onLogout={onLogout}
          onProfileClick={onNavigateToProfile}
          onMessagesClick={onNavigateToMessages}
          onNavigateToCreateProduct={onNavigateToCreateProduct}
          onAdministrationClick={onNavigateToAdministration}
        />
        <main className="product-detail-error-container">
          <EmptyState
            icon={<PackageSearch />}
            title={t.productDetail.notFound}
            description={t.productDetail.notFoundDescription}
          >
            <button className="app-empty-state-primary" type="button" onClick={onNavigateToHome}>
              <House size={17} aria-hidden="true" />
              {t.productDetail.notFoundAction}
            </button>
          </EmptyState>
        </main>
        <Footer
          onNavigateToTerms={onNavigateToTerms}
          onNavigateToPrivacy={onNavigateToPrivacy}
          onNavigateToCookies={onNavigateToCookies}
          onNavigateToContact={onNavigateToContact}
        />
      </div>
    );
  }

  const sellerPhoneNumber = product.user.phoneNumber?.trim() || '';
  const sellerPhoneDisplay = showPhoneNumber && isLoggedIn
    ? sellerPhoneNumber || t.productDetail.phoneUnavailable
    : '****';

  return (
    <div className="product-detail-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onSettingsClick={onNavigateToSettings}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
        onAdministrationClick={onNavigateToAdministration}
      />

      {showRatingConfirm && pendingRating && (
        <div className="product-detail-rating-confirm-overlay" onClick={cancelRating}>
          <div className="product-detail-rating-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="product-detail-rating-confirm-title">{t.productDetail.ratingConfirmTitle}</h3>
            <p className="product-detail-rating-confirm-message">
              {pendingRating.type === 'product'
                ? t.productDetail.ratingConfirmProduct.replace('{rating}', pendingRating.value.toString())
                : t.productDetail.ratingConfirmSeller.replace('{rating}', pendingRating.value.toString())}
            </p>
            <div className="product-detail-rating-confirm-actions">
              <button className="product-detail-rating-confirm-cancel" onClick={cancelRating}>
                {t.common.no}
              </button>
              <button className="product-detail-rating-confirm-yes" onClick={confirmRating}>
                {t.common.yes}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReportModal && (
        <ReportModal onClose={handleReportClose} onSubmit={handleReportSubmit} />
      )}

      {showReportUserModal && (
        <ReportUserModal onClose={handleReportUserClose} onSubmit={handleReportUserSubmit} />
      )}

      {showImageViewer && availableImages.length > 0 && (
        <ImageViewer
          images={availableImages.map(img => img.imageUrl)}
          initialIndex={currentImageIndex}
          onClose={() => setShowImageViewer(false)}
        />
      )}

      <main className="product-detail-content">
        <div className="product-detail-back-breadcrumbs-container">
          <BackButton className="product-detail-back-button" onClick={() => window.history.back()} />
          <Breadcrumbs
            homeLabel={t.common.home}
            navigationLabel={t.common.navigation}
            onNavigateHome={onNavigateToHome}
            items={[
              ...(product.category ? [{
                label: product.category.name,
                href: createPageUrl('search', {
                  categoryId: product.category.id,
                  productType: product.productType,
                  negotiationType: product.negotiable ? 'NEGOTIABLE' : 'NON_NEGOTIABLE'
                })
              }] : []),
              ...(product.subCategory ? [{
                label: product.subCategory.name,
                href: createPageUrl('search', {
                  categoryId: product.category?.id || '',
                  subCategoryId: product.subCategory.id,
                  productType: product.productType,
                  negotiationType: product.negotiable ? 'NEGOTIABLE' : 'NON_NEGOTIABLE'
                })
              }] : []),
              { label: product.title }
            ]}
          />
        </div>

        <div className="product-detail-main-section">
          <div className="product-detail-left-section">
            <div className="product-detail-images-container">
            <div
              className="product-detail-main-image-wrapper"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {availableImages[currentImageIndex]?.imageUrl ? (
                <img
                  src={availableImages[currentImageIndex].imageUrl}
                  alt={product.title}
                  className="product-detail-main-image"
                  onClick={() => setShowImageViewer(true)}
                  style={{ cursor: 'pointer' }}
                />
              ) : (
                <ProductImagePlaceholder
                  className="product-detail-image-placeholder"
                  label={t.productDetail.noImage}
                />
              )}

              {availableImages.length > 0 && (
                <div className="product-detail-image-counter">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M2 0H10C11.1027 0 12 0.897333 12 2V10C12 11.1027 11.1027 12 10 12H2C0.897333 12 0 11.1027 0 10V2C0 0.897333 0.897333 0 2 0ZM2 1.33333H10C10.368 1.33333 10.6667 1.632 10.6667 2V7.576L8.53133 5.75467C7.87133 5.19333 6.83867 5.19333 6.18467 5.75067L1.33333 9.79867V2C1.33333 1.632 1.632 1.33333 2 1.33333ZM4.33333 3.66667C4.33333 4.21867 3.88533 4.66667 3.33333 4.66667C2.78133 4.66667 2.33333 4.21867 2.33333 3.66667C2.33333 3.11467 2.78133 2.66667 3.33333 2.66667C3.88533 2.66667 4.33333 3.11467 4.33333 3.66667ZM2.374 10.6667H10C10.368 10.6667 10.6667 10.368 10.6667 10V9.32933L7.666 6.76933C7.50533 6.63133 7.208 6.63067 7.044 6.77L2.374 10.6667Z" fill="white" transform="translate(2, 2)"/>
                  </svg>
                  <span>{currentImageIndex + 1}/{availableImages.length}</span>
                </div>
              )}

              {availableImages.length > 0 && <button
                className="product-detail-image-nav-button product-detail-image-nav-left"
                onClick={handlePreviousImage}
                disabled={currentImageIndex === 0}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.0005 4.99993C14.2265 4.99993 14.4535 5.07593 14.6405 5.23193C15.0645 5.58493 15.1215 6.21593 14.7685 6.63993L10.2925 12.0109L14.6075 17.3729C14.9535 17.8029 14.8855 18.4329 14.4555 18.7789C14.0245 19.1249 13.3955 19.0569 13.0485 18.6269L8.22051 12.6269C7.92251 12.2559 7.92651 11.7259 8.23151 11.3599L13.2315 5.35993C13.4295 5.12293 13.7135 4.99993 14.0005 4.99993Z" fill="#596255"/>
                </svg>
              </button>}

              {availableImages.length > 0 && <button
                className="product-detail-image-nav-button product-detail-image-nav-right"
                onClick={handleNextImage}
                disabled={currentImageIndex === availableImages.length - 1}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.9995 19.0001C9.7735 19.0001 9.5465 18.9241 9.3595 18.7681C8.9355 18.4151 8.8785 17.7841 9.2315 17.3601L13.7075 11.9891L9.3925 6.62707C9.0465 6.19707 9.1145 5.56707 9.5445 5.22107C9.9755 4.87507 10.6045 4.94307 10.9515 5.37307L15.7795 11.3731C16.0775 11.7441 16.0735 12.2741 15.7685 12.6401L10.7685 18.6401C10.5705 18.8771 10.2865 19.0001 9.9995 19.0001Z" fill="#071105"/>
                </svg>
              </button>}
            </div>
          </div>

          <div className="product-detail-info-section">
            <div className="product-detail-type-section">
              <div className={`product-detail-type-label ${product.productType === 'DEMAND' ? 'product-detail-type-label-buy' : 'product-detail-type-label-sell'}`}>
                <span className="product-detail-type-caption">{t.productDetail.listingType}</span>
                <span className="product-detail-type-value">
                  {product.productType === 'DEMAND'
                    ? t.productDetail.wantToBuy
                    : t.productDetail.wantToSell}
                </span>
              </div>
              <div className="product-detail-header-actions">
                {!isAdmin && (
                  <button
                    type="button"
                    className={`product-detail-favorite-button ${isFavorite ? 'active' : ''}`}
                    onClick={handleFavoriteProduct}
                    disabled={favoriteUpdating}
                    aria-pressed={isFavorite}
                    aria-label={isFavorite ? t.productCard.removeFavorite : t.productCard.addFavorite}
                    title={isFavorite ? t.productCard.removeFavorite : t.productCard.addFavorite}
                  >
                    <Heart size={16} fill={isFavorite ? 'currentColor' : 'none'} aria-hidden="true" />
                  </button>
                )}
                <button
                  type="button"
                  className="product-detail-share-button"
                  onClick={handleShareProduct}
                  aria-label={t.productDetail.share}
                  title={t.productDetail.share}
                >
                  <Share2 size={16} strokeWidth={2} aria-hidden="true" />
                  <span>{t.productDetail.share}</span>
                </button>
              </div>
            </div>

            <div className="product-detail-title-price-container">
              <h1 className="product-detail-title">{product.title}</h1>
              <div className="product-detail-price-container">
                <div className="product-detail-price">
                  {product.price.price} {product.price.currency}/{formatUnitType(product.price.unitType)}
                </div>
                {product.negotiable && <div className="product-detail-negotiable-label">{t.productDetail.negotiable}</div>}
              </div>
            </div>

            <div className="product-detail-divider"></div>

            <div className="product-detail-rating-section">
              <div className="product-detail-current-rating">
                <Rating
                  initialValue={product.rate / 5}
                  readonly={true}
                  iconsCount={1}
                  size={24}
                  fillColor="#267200"
                  emptyColor="#D5DED1"
                  allowFraction={true}
                />
                <span className="product-detail-rating-value">{product.rate.toFixed(1)}</span>
              </div>

              <div className="product-detail-rating-actions">
                <span className="product-detail-rating-label">{t.productDetail.leaveRating}</span>
                <div className="product-detail-rating-stars">
                  <Rating
                    key={`product-rating-${productId}-${productRating}`}
                    onClick={handleProductRating}
                    initialValue={productRating}
                    size={24}
                    fillColor="#267200"
                    emptyColor="#D5DED1"
                    allowFraction={true}
                    transition={true}
                  />
                </div>
              </div>
            </div>

            <div className="product-detail-divider"></div>

            <section className="product-detail-description-section" aria-labelledby="product-detail-description-title">
              <div className="product-detail-description-header">
                <h2 id="product-detail-description-title">{t.productDetail.descriptionTitle}</h2>
              </div>
              <div className="product-detail-description">
                {product.description}
              </div>
            </section>

            <div className="product-detail-time-report-container">
              <div className="product-detail-publication-meta">
                <div className="product-detail-time-submitted">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path fillRule="evenodd" clipRule="evenodd" d="M0 6.66667C0 2.99067 2.99067 0 6.66667 0C10.3427 0 13.3333 2.99067 13.3333 6.66667C13.3333 10.3427 10.3427 13.3333 6.66667 13.3333C2.99067 13.3333 0 10.3427 0 6.66667ZM6.66667 12C3.726 12 1.33333 9.60733 1.33333 6.66667C1.33333 3.726 3.726 1.33333 6.66667 1.33333C9.60733 1.33333 12 3.726 12 6.66667C12 9.60733 9.60733 12 6.66667 12ZM9.33333 6H7.33333V4C7.33333 3.63133 7.03467 3.33333 6.66667 3.33333C6.29867 3.33333 6 3.63133 6 4V6.66667C6 7.03533 6.29867 7.33333 6.66667 7.33333H9.33333C9.702 7.33333 10 7.03533 10 6.66667C10 6.298 9.702 6 9.33333 6Z" fill="#596255" transform="translate(1, 1)"/>
                  </svg>
                  <span className="product-detail-time-label">{formatDate(getPublishedAt(product))}</span>
                </div>
                <span className="product-detail-meta-separator" aria-hidden="true">•</span>
                <div className="product-detail-view-count">
                  <Eye size={16} aria-hidden="true" />
                  <span className="product-detail-time-label">{formatViewCount(product.viewCount ?? 0)}</span>
                </div>
              </div>

              <div className="product-detail-report-button" onClick={handleReportClick}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M9.11267 0.951333C9.472 0.834 9.87067 0.897333 10.1787 1.12C10.4847 1.342 10.6667 1.69733 10.6667 2.07067V7.83267C10.6667 8.268 10.422 8.66533 10.0273 8.87C9.61533 9.08133 8.87267 9.33333 7.66667 9.33333C6.66657 9.33333 5.86559 9.01969 5.09133 8.71652L5.09 8.716L5.08848 8.71541C4.39971 8.44562 3.74878 8.19067 3 8.19067C2.11533 8.19067 1.59667 8.35067 1.33333 8.468V11.3333C1.33333 11.702 1.03533 12 0.666667 12C0.298 12 0 11.702 0 11.3333V8.09533V6V1.238C0 1.05933 0.072 0.887333 0.2 0.762667C0.33 0.634667 1.07533 0 3 0C4.00009 0 4.80108 0.31364 5.57533 0.616811L5.57667 0.617333L5.57819 0.617931C6.26696 0.88771 6.91789 1.14267 7.66667 1.14267C8.22867 1.14267 8.72867 1.076 9.11267 0.951333ZM7.66667 8C8.55 8 9.06933 7.842 9.33333 7.724V2.276C8.85067 2.40867 8.292 2.476 7.66667 2.476C6.66657 2.476 5.86559 2.16236 5.09133 1.85919L5.09 1.85867L5.08847 1.85807C4.3997 1.58829 3.74878 1.33333 3 1.33333C2.118 1.33333 1.59733 1.49267 1.33333 1.61067V6V7.056C1.74933 6.942 2.29533 6.85733 3 6.85733C4.00009 6.85733 4.80108 7.17097 5.57534 7.47415L5.57667 7.47467L5.57819 7.47526C6.26696 7.74504 6.91789 8 7.66667 8Z" fill="#B12A00" transform="translate(3, 2)"/>
                </svg>
                <span className="product-detail-report-label">{t.productDetail.report}</span>
              </div>
            </div>

            <div className="product-detail-divider" aria-hidden="true"></div>
          </div>
          </div>

          <div className="product-detail-seller-card">
            <div className="product-detail-seller-header">
              <div className="product-detail-seller-avatar">
                {product.user.imageUrl ? (
                  <img src={product.user.imageUrl} alt={`${product.user.firstName} ${product.user.lastName}`} className="product-detail-avatar-image" />
                ) : (
                  <div className="product-detail-avatar-placeholder">
                    {product.user.firstName.charAt(0)}{product.user.lastName.charAt(0)}
                  </div>
                )}
              </div>
              <div className="product-detail-seller-identity">
                <a
                  className="product-detail-seller-name"
                  href={createPageUrl('userDetails', {
                    id: product.user.id,
                    name: getUserPublicName(product.user),
                  })}
                  onClick={appLinkClick(() => onNavigateToUserDetails?.(
                    product.user.id,
                    getUserPublicName(product.user),
                  ))}
                >
                  {product.user.firstName} {product.user.lastName}
                </a>
              </div>
            </div>
            <AccountTypeInfo accountType={product.user.accountType} companyDetails={product.user} />

            <div className="product-detail-seller-phone">
              <Phone aria-hidden="true" />
              <span className="product-detail-masked-phone">
                {sellerPhoneDisplay}
              </span>
              <button
                type="button"
                className="product-detail-show-phone-button"
                onClick={() => {
                  if (!isLoggedIn) {
                    onNavigateToLogin();
                  } else {
                    if (!showPhoneNumber && sellerPhoneNumber && !phoneRevealTrackedRef.current
                      && product.user.id !== getUserId()) {
                      phoneRevealTrackedRef.current = true;
                      trackEvent('contact_seller', {
                        ...listingAnalyticsParams(product),
                        contact_method: 'phone_reveal',
                        source_surface: 'listing',
                      });
                    }
                    setShowPhoneNumber((currentValue) => !currentValue);
                  }
                }}
              >
                {showPhoneNumber && isLoggedIn ? t.productDetail.hidePhone : t.productDetail.showPhone}
              </button>
            </div>

            <div className="product-detail-divider"></div>

            <div className="product-detail-seller-message">
              <label className="product-detail-message-label">{t.productDetail.sendMessage}</label>
              <div className="product-detail-message-input-wrapper">
                <textarea
                  className="product-detail-message-input"
                  placeholder={t.productDetail.messagePlaceholder}
                  value={messageText}
                  maxLength={MAX_PRODUCT_DETAIL_MESSAGE_LENGTH}
                  onChange={(e) => {
                    if (pendingMessageRef.current?.message !== e.target.value) {
                      pendingMessageRef.current = null;
                    }
                    setMessageText(e.target.value);
                  }}
                  disabled={messageSending}
                ></textarea>
                <span className="product-detail-message-character-count">
                  {messageText.length}/{MAX_PRODUCT_DETAIL_MESSAGE_LENGTH}
                </span>
                <button
                  type="button"
                  className="product-detail-send-message-button"
                  onClick={handleSendMessage}
                  disabled={messageSending}
                  aria-label={t.productDetail.sendMessage}
                >
                  <Send size={18} aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="product-detail-divider"></div>

            <div className="product-detail-seller-location">
              <div className="product-detail-location-header">
                <span className="product-detail-location-label">{t.productDetail.location}</span>
                <div className="product-detail-location-display">
                  <MapPin aria-hidden="true" />
                  <span className="product-detail-location-text">{product.locationDto.name}, {product.locationDto.county.name}</span>
                </div>
              </div>
              <div className="product-detail-location-map" ref={mapContainerRef}>
                <div className="product-detail-map-zoom-controls">
                  <button type="button" className="product-detail-zoom-button" onClick={handleZoomIn} aria-label="Mărește harta">
                    <Plus size={18} aria-hidden="true" />
                  </button>
                  <div className="product-detail-zoom-divider"></div>
                  <button type="button" className="product-detail-zoom-button" onClick={handleZoomOut} aria-label="Micșorează harta">
                    <Minus size={18} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>

            <div className="product-detail-divider"></div>

            <div className="product-detail-seller-rating-section">
              <div className="product-detail-rating-header">
                <span className="product-detail-rating-label-seller">{t.productDetail.rateSellerLabel}</span>
              </div>
              <div className="product-detail-seller-rating-controls">
                <div className="product-detail-current-seller-rating">
                  <Rating
                    initialValue={product.user.rate / 5}
                    readonly={true}
                    iconsCount={1}
                    size={24}
                    fillColor="#267200"
                    emptyColor="#D5DED1"
                    allowFraction={true}
                  />
                  <span className="rating-value">{product.user.rate?.toFixed(2) || '4.75'}</span>
                </div>
                <div className="product-detail-seller-rating-stars">
                  <Rating
                    key={`seller-rating-${product.user.id}-${sellerRating}`}
                    onClick={handleSellerRating}
                    initialValue={sellerRating}
                    size={24}
                    fillColor="#267200"
                    emptyColor="#D5DED1"
                    allowFraction={true}
                    transition={true}
                  />
                </div>
              </div>
            </div>
            <p className="product-detail-rating-disclosure">{t.productDetail.ratingNotVerified}</p>

            <div className="product-detail-divider" style={{ marginTop: '8px', marginBottom: '8px' }}></div>

            <div className="product-detail-report-button" onClick={handleReportUserClick}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M9.11267 0.951333C9.472 0.834 9.87067 0.897333 10.1787 1.12C10.4847 1.342 10.6667 1.69733 10.6667 2.07067V7.83267C10.6667 8.268 10.422 8.66533 10.0273 8.87C9.61533 9.08133 8.87267 9.33333 7.66667 9.33333C6.66657 9.33333 5.86559 9.01969 5.09133 8.71652L5.09 8.716L5.08848 8.71541C4.39971 8.44562 3.74878 8.19067 3 8.19067C2.11533 8.19067 1.59667 8.35067 1.33333 8.468V11.3333C1.33333 11.702 1.03533 12 0.666667 12C0.298 12 0 11.702 0 11.3333V8.09533V6V1.238C0 1.05933 0.072 0.887333 0.2 0.762667C0.33 0.634667 1.07533 0 3 0C4.00009 0 4.80108 0.31364 5.57533 0.616811L5.57667 0.617333L5.57819 0.617931C6.26696 0.88771 6.91789 1.14267 7.66667 1.14267C8.22867 1.14267 8.72867 1.076 9.11267 0.951333ZM7.66667 8C8.55 8 9.06933 7.842 9.33333 7.724V2.276C8.85067 2.40867 8.292 2.476 7.66667 2.476C6.66657 2.476 5.86559 2.16236 5.09133 1.85919L5.09 1.85867L5.08847 1.85807C4.3997 1.58829 3.74878 1.33333 3 1.33333C2.118 1.33333 1.59733 1.49267 1.33333 1.61067V6V7.056C1.74933 6.942 2.29533 6.85733 3 6.85733C4.00009 6.85733 4.80108 7.17097 5.57534 7.47415L5.57667 7.47467L5.57819 7.47526C6.26696 7.74504 6.91789 8 7.66667 8Z" fill="#B12A00" transform="translate(3, 2)"/>
              </svg>
              <span className="product-detail-report-label">Raportează</span>
            </div>
          </div>
        </div>

        <ProductCarousel
          title={t.productDetail.moreFromSeller}
          productType="PRODUCT"
          userId={product?.user.id}
          excludedIds={product ? [product.id] : []}
          emptyTitle={t.productDetail.moreFromSellerEmptyTitle}
          emptyMessage={t.productDetail.moreFromSellerEmptyDescription}
          emptyActionLabel={t.productDetail.moreFromSellerEmptyAction}
          onNavigateToProductDetail={onNavigateToProductDetail}
          isLoggedIn={isLoggedIn}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToSearch={() => {
            const params = new URLSearchParams({
              productType: 'PRODUCT'
            });
            window.location.href = createPageUrl(
              'search',
              Object.fromEntries(params.entries())
            );
          }}
          showViewAll={false}
          className="product-detail-carousel-wrapper"
        />
      </main>

      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToContact={onNavigateToContact}
      />
    </div>
  );
};

export default ProductDetail;
